"use client";

import { useCallback } from "react";
import { useAuthPrompt } from "@/components/auth/auth-prompt-provider";
import { useCurrentHref } from "@/hooks/use-current-href";
import { useShellProfileData } from "@/hooks/use-shell-profile-data";

type GateCopy = {
  /** Shown on the sign-in branch. The finish-profile branch has its own copy. */
  title?: string;
  description?: string;
};

/**
 * Action-level sibling of `useAuthGatedLink`, for member-only *actions* that are
 * not a navigation — starting a movement track, saving one, and so on.
 *
 * The routes that live outside the `(with-navbar)` shell (the mini-apps) never
 * run `<OnboardingGate/>`, and their server actions authenticate on session
 * alone. A viewer who signed in but stopped partway through onboarding
 * therefore passed every check these screens made: they could start a run and
 * save it with no name, no photo, and no location, which is the state store
 * review found. Gating on `profileComplete` (never `needsOnboarding` — see the
 * note on `CurrentShellProfile`) closes that without taking guest browse away.
 *
 * Returns `true` when the caller may proceed. Otherwise it has already opened
 * the prompt with the intent that matches what is actually missing, and the
 * caller should return.
 */
export function useProfileGatedAction(copy?: GateCopy) {
  const { openAuthPrompt } = useAuthPrompt();
  const { data: shellProfile, isPending } = useShellProfileData();
  const currentHref = useCurrentHref();

  return useCallback(
    (options?: { callbackURL?: string }) => {
      const callbackURL = options?.callbackURL ?? currentHref;

      // Still resolving: treat as not-signed-in rather than letting the action
      // through. A cold native launch spends its first seconds here, and the
      // prompt is recoverable where a half-started activity is not.
      if (isPending) {
        openAuthPrompt({
          intent: "sign-in",
          title: copy?.title,
          description: copy?.description,
          callbackURL,
        });
        return false;
      }

      if (!shellProfile) {
        openAuthPrompt({
          intent: "sign-in",
          title: copy?.title,
          description: copy?.description,
          callbackURL,
        });
        return false;
      }

      if (!shellProfile.profileComplete) {
        openAuthPrompt({ intent: "finish-profile", callbackURL });
        return false;
      }

      return true;
    },
    [
      copy?.description,
      copy?.title,
      currentHref,
      isPending,
      openAuthPrompt,
      shellProfile,
    ],
  );
}
